import { DateTime } from 'luxon'
import {
  PAYMENT_RELEASE_MAX_HOURS_AHEAD,
  PAYMENT_RELEASE_TIME_ZONE,
} from 'src/constants/invoice/constants'
import { PaymentReleaseDateProblem } from 'src/constants/invoice/enums'
import type { ReleaseWallClock } from 'src/interfaces/invoice'

const padTwo = (value: number) => {
  return String(value).padStart(2, '0')
}

/**
 * The `releaseAt` wire value: the wall clock exactly as picked, `YYYY-MM-DD HH:mm`.
 *
 * Built from the components, NOT by formatting a resolved Luxon instant — a time in the skipped
 * spring-forward hour would otherwise be sent already shifted. The server owns that resolution
 * (skipped hour → forward an hour, repeated hour → earlier instant), so it gets the raw pick.
 */
export const formatReleaseAt = (wallClock: ReleaseWallClock): string => {
  const date = `${wallClock.year}-${padTwo(wallClock.month)}-${padTwo(wallClock.day)}`

  return `${date} ${padTwo(wallClock.hour)}:${padTwo(wallClock.minute)}`
}

/**
 * The instant a Central Time wall clock resolves to, the way the server resolves it. Invalid for an
 * impossible date.
 *
 * A time in the repeated fall-back hour happens twice; Luxon on its own picks the offset closest to
 * today's, so the answer would change with the date. The server always takes the earlier instant,
 * so this does too. A time in the skipped spring-forward hour is already moved forward by Luxon.
 */
export const toCentralInstant = (wallClock: ReleaseWallClock): DateTime => {
  const instant = DateTime.fromObject(wallClock, { zone: PAYMENT_RELEASE_TIME_ZONE })

  return instant.getPossibleOffsets().reduce((earliest, candidate) => {
    return candidate < earliest ? candidate : earliest
  })
}

/** The Central Time wall clock of an instant — `now` by default. */
export const toCentralWallClock = (instant: DateTime = DateTime.now()): ReleaseWallClock => {
  const central = instant.setZone(PAYMENT_RELEASE_TIME_ZONE)

  return {
    year: central.year,
    month: central.month,
    day: central.day,
    hour: central.hour,
    minute: central.minute,
  }
}

/** The latest wall clock a hold can be placed at, so the picker is bounded in Central Time. */
export const getLatestReleaseWallClock = (now: DateTime = DateTime.now()): ReleaseWallClock => {
  return toCentralWallClock(now.plus({ hours: PAYMENT_RELEASE_MAX_HOURS_AHEAD }))
}

/**
 * Mirrors the server's rule: strictly in the future, at most 30 days (720 hours) ahead. A pass
 * here does not guarantee the server agrees near either boundary — the 422 is still handled.
 */
export const validateReleaseWallClock = (
  wallClock: ReleaseWallClock,
  now: DateTime = DateTime.now(),
): PaymentReleaseDateProblem | null => {
  const instant = toCentralInstant(wallClock)

  if (!instant.isValid) {
    return PaymentReleaseDateProblem.Invalid
  }

  if (instant <= now) {
    return PaymentReleaseDateProblem.Past
  }

  if (instant > now.plus({ hours: PAYMENT_RELEASE_MAX_HOURS_AHEAD })) {
    return PaymentReleaseDateProblem.TooFar
  }

  return null
}

/** Parses `paymentReleaseScheduledAt`, which the server stores and returns as a UTC instant. */
export const parseScheduledAt = (scheduledAt: string | null | undefined): DateTime | null => {
  if (!scheduledAt) {
    return null
  }

  const instant = DateTime.fromISO(scheduledAt, { zone: 'utc' })

  return instant.isValid ? instant : null
}

/**
 * `paymentReleaseScheduledAt` rendered back in Central Time with its abbreviation, e.g.
 * `Nov 14, 2026, 9:00 AM CST` — the same wall clock the admin picked, not the UTC echo.
 */
export const formatScheduledAtInCentral = (
  scheduledAt: string | null | undefined,
): string | null => {
  const instant = parseScheduledAt(scheduledAt)

  if (!instant) {
    return null
  }

  return instant.setZone(PAYMENT_RELEASE_TIME_ZONE).toFormat('LLL d, yyyy, h:mm a ZZZZ')
}

/** A resolved instant in the admin's own zone, e.g. `Nov 14, 4:00 PM GMT+1`. */
export const formatInstantInLocalZone = (instant: DateTime): string => {
  return instant.toLocal().toFormat('LLL d, h:mm a ZZZZ')
}

/** Whether the admin's own zone currently has the same offset as Central Time. */
export const isLocalZoneCentral = (now: DateTime = DateTime.now()): boolean => {
  return now.toLocal().offset === now.setZone(PAYMENT_RELEASE_TIME_ZONE).offset
}
